import process from 'node:process';
import {
  getArgValue,
  hasFlag,
  resolveConfig,
  readAllProfiles,
  findSessionByOrg,
  setDefaultProfile,
  printAmbiguousOrg,
  type StoredAuth,
} from './auth';
import { decodeJwtPayload } from './jwt.js';

type OrgsResponse = {
  ok: boolean;
  currentOrgId?: string;
  organizations?: Array<{ id: string; name: string; role: string }>;
  error?: string;
};

type SuperadminOrgsResponse = {
  ok: boolean;
  organizations?: Array<{ id: string; name: string; memberCount?: number }>;
  error?: string;
};

type OrgMembersResponse = {
  ok: boolean;
  error?: string;
  items?: Array<{
    userId: string;
    displayName: string | null;
    primaryEmail: string | null;
    role: string;
    isActive: boolean;
    slackLinked: boolean;
  }>;
};

/**
 * `cofactor org members` — list members of the current org, annotated with
 * whether each has linked their Slack user. Powers recipient lookup for the
 * `utilities email` / `utilities slack` commands.
 */
async function runOrgMembers(argv: string[]) {
  const { apiUrl, token } = await resolveConfig(argv);
  const res = await fetch(`${apiUrl}/org/members`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    console.error('Failed to fetch members. You may need to re-login: cofactor login');
    process.exit(1);
  }
  const data = (await res.json()) as OrgMembersResponse;
  if (!data.ok || !data.items) {
    console.error('Failed to fetch members:', data.error ?? 'Unknown error');
    process.exit(1);
  }

  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(data.items, null, 2));
    return;
  }

  if (data.items.length === 0) {
    console.log('No members.');
    return;
  }
  console.log('Members (✓ = Slack-linked, can receive utilities slack DMs):\n');
  for (const m of data.items) {
    const slack = m.slackLinked ? '✓' : ' ';
    const name = m.displayName ?? '(no name)';
    const email = m.primaryEmail ?? '(no email)';
    console.log(`  ${slack}  ${name}  <${email}>  [${m.role}]`);
  }
}

/** Short "in ~N days" / "expired" rendering of a JWT `exp` (seconds). */
function fmtExpShort(expSec: number | undefined): string {
  if (!expSec) return '';
  const deltaSec = expSec - Math.floor(Date.now() / 1000);
  if (deltaSec < 0) return 'expired';
  const days = Math.round(deltaSec / 86400);
  return days >= 1 ? `~${days}d left` : '<1d left';
}

/**
 * `cofactor org use <name|id>` — point the persistent default at one of your
 * logged-in sessions, so bare (no `--org`) commands act as that org.
 */
/** Value-taking global flags — used to skip a flag's value when scanning for a
 * positional argument (so `org use --env dev acme` picks `acme`, not `dev`). */
const VALUE_FLAGS = new Set(['--env', '--api-url', '--token', '--org']);

/** First positional token, skipping flags, their values, and any `skip` words. */
function firstPositional(argv: string[], skip: Set<string>): string | undefined {
  for (let i = 0; i < argv.length; i++) {
    const a = argv[i];
    if (a.startsWith('-')) continue;
    if (i > 0 && VALUE_FLAGS.has(argv[i - 1])) continue; // this token is a flag value
    if (skip.has(a)) continue;
    return a;
  }
  return undefined;
}

async function runOrgUse(argv: string[]) {
  const selector = firstPositional(argv, new Set(['use']));
  if (!selector) {
    console.error('Usage: canary org use <name|id>');
    process.exit(1);
  }
  const env = getArgValue(argv, '--env');
  const match = await findSessionByOrg(selector, env);
  if (match.kind === 'none') {
    console.error(`No logged-in session for org "${selector}".`);
    console.error(`Add one with: canary login --org "${selector}"`);
    process.exit(1);
  }
  if (match.kind === 'ambiguous') {
    printAmbiguousOrg(selector, match.candidates);
    process.exit(1);
  }
  await setDefaultProfile(match.key);
  const envLabel = match.auth.envLabel ? ` [${match.auth.envLabel}]` : '';
  console.log(`Active org set to ${match.auth.orgName ?? match.auth.orgId}${envLabel}.`);
  console.log('Bare commands (no --org) will now act as this org.');
}

export async function runOrgs(argv: string[]) {
  // Sub-commands: `orgs members`, `org use <name|id>`. Everything else lists.
  const sub = argv.find((a) => !a.startsWith('-'));
  if (sub === 'members') {
    return runOrgMembers(argv.filter((a) => a !== 'members'));
  }
  if (sub === 'use') {
    return runOrgUse(argv);
  }

  const { apiUrl, token } = await resolveConfig(argv);
  // The "* = active" marker reflects the org we actually resolved for this
  // invocation (honors --org / --env / default), read straight off the token.
  const resolvedOrgId = (() => {
    const payload = decodeJwtPayload(token);
    return typeof payload?.org_id === 'string' ? payload.org_id : undefined;
  })();
  const showAll = hasFlag(argv, '--all');

  if (showAll) {
    // Superadmin: list all organizations
    const res = await fetch(`${apiUrl}/superadmin/organizations`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      if (res.status === 403) {
        console.error('Superadmin access required for --all');
      } else {
        console.error('Failed to fetch organizations. You may need to re-login: canary login');
      }
      process.exit(1);
    }

    const data = (await res.json()) as SuperadminOrgsResponse;
    if (!data.ok || !data.organizations) {
      console.error('Failed to fetch organizations:', data.error ?? 'Unknown error');
      process.exit(1);
    }

    console.log('All Organizations:\n');
    for (const org of data.organizations) {
      const members = org.memberCount !== undefined && org.memberCount !== null ? ` (${org.memberCount} members)` : '';
      console.log(`  ${org.name}${members} [${org.id}]`);
    }

    console.log(`\nTotal: ${data.organizations.length} organizations`);
    return;
  }

  const res = await fetch(`${apiUrl}/cli-login/orgs`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    console.error('Failed to fetch organizations. You may need to re-login: canary login');
    process.exit(1);
  }

  const data = (await res.json()) as OrgsResponse;
  if (!data.ok || !data.organizations) {
    console.error('Failed to fetch organizations:', data.error ?? 'Unknown error');
    process.exit(1);
  }

  const currentOrgId = resolvedOrgId ?? data.currentOrgId;

  // Map orgId → stored session (env-mirror + org-keyed profiles share an orgId;
  // keep the first — they hold the same token).
  const { profiles } = await readAllProfiles();
  const sessionByOrg = new Map<string, StoredAuth>();
  for (const { auth: a } of profiles) {
    if (a.orgId && !sessionByOrg.has(a.orgId)) sessionByOrg.set(a.orgId, a);
  }

  console.log('Organizations:\n');
  for (const org of data.organizations) {
    const marker = org.id === currentOrgId ? ' *' : '';
    const session = sessionByOrg.get(org.id);
    let sessionInfo: string;
    if (session?.token) {
      const payload = decodeJwtPayload(session.token);
      const exp = typeof payload?.exp === 'number' ? payload.exp : undefined;
      const parts = [session.envLabel, fmtExpShort(exp)].filter(Boolean);
      sessionInfo = `  — logged in${parts.length ? ` (${parts.join(', ')})` : ''}`;
    } else {
      sessionInfo = '  — no local session';
    }
    console.log(`  ${org.name} (${org.role}) [${org.id}]${marker}${sessionInfo}`);
  }

  console.log('\n* = active org (used when no --org is given)');
  console.log('Run one command as an org:  canary <command> --org <name>');
  console.log('Switch the active org:      canary org use <name>');
  console.log('Add / refresh a session:    canary login --org <name>');
}
